import { useState, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Phone,
  ShieldCheck,
  RefreshCw,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@project/components/ui/input-otp";

export function LoginPage() {
  const { requestLoginOtp, verifyLoginOtp } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirectTo?: string };
  const redirectTo = search.redirectTo ?? "/";

  const [step, setStep] = useState<1 | 2>(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // OTP Timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (cleanPhone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setLoading(true);
    try {
      const codeRef = await requestLoginOtp(cleanPhone);
      setCodeReference(codeRef);
      setStep(2);
      setTimer(30);
      setCanResend(false);
      toast.success("OTP sent to your phone number");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) {
      return;
    }
    setLoading(true);
    try {
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      const codeRef = await requestLoginOtp(cleanPhone);
      setCodeReference(codeRef);
      setTimer(30);
      setCanResend(false);
      toast.success("OTP resent successfully");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await verifyLoginOtp(otp, codeReference);
      if (redirectTo.includes("?")) {
        const [path, searchStr] = redirectTo.split("?");
        const searchParams = new URLSearchParams(searchStr);
        const searchObj: Record<string, string> = {};
        searchParams.forEach((val, key) => {
          searchObj[key] = val;
        });
        navigate({ to: path as any, search: searchObj as any });
      } else {
        navigate({ to: redirectTo as any });
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Card Container */}
          <div className="border-border/80 relative overflow-hidden rounded-3xl border bg-card p-8 shadow-xl transition-all">
            {/* Top Decorative Strip */}
            <div className="bg-tricolor absolute left-0 right-0 top-0 h-1.5" />

            <div className="mb-6 text-center">
              <div className="bg-primary/10 mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl text-primary">
                {step === 1 ? (
                  <Phone className="h-7 w-7" />
                ) : (
                  <KeyRound className="h-7 w-7" />
                )}
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                {step === 1 ? "Sign In to Jivandhara" : "Verify Phone Number"}
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {step === 1
                  ? "Enter your mobile number to receive a verification code"
                  : `Enter the 6-digit OTP sent to +91 ${phoneNumber}`}
              </p>
            </div>

            {step === 1 ? (
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="focus-within:ring-primary/30 mt-2 flex rounded-xl border border-input bg-background shadow-sm focus-within:ring-2">
                    <span className="flex items-center rounded-l-xl border-r border-input bg-muted px-3.5 text-sm font-medium text-muted-foreground">
                      +91
                    </span>
                    <Input
                      id="phone"
                      type="tel"
                      maxLength={10}
                      placeholder="7698343695"
                      value={phoneNumber}
                      onChange={e =>
                        setPhoneNumber(e.target.value.replace(/\D/g, ""))
                      }
                      className="rounded-l-none border-0 focus-visible:ring-0"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || phoneNumber.length !== 10}
                  className="w-full rounded-xl py-6 font-semibold shadow-md"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Get OTP Code <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="pt-2 text-center text-sm text-muted-foreground">
                  Don&apos;t have an account?{" "}
                  <Link
                    to="/signup"
                    search={{ redirectTo }}
                    className="font-semibold text-primary hover:underline"
                  >
                    Create Account
                  </Link>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div className="flex justify-center py-2">
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={otp}
                    onChange={value => setOtp(value)}
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot
                        index={0}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={1}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={2}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={3}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={4}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={5}
                        className="rounded-lg border shadow-sm"
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {timer > 0
                      ? `Resend code in 00:${timer < 10 ? `0${timer}` : timer}`
                      : "Didn't get code?"}
                  </span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={!canResend || loading}
                    className="flex items-center font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Resend OTP
                  </button>
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full rounded-xl py-6 font-semibold shadow-md"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Verify & Continue{" "}
                        <ShieldCheck className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex w-full items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="mr-1.5 h-4 w-4" /> Change Phone Number
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
